import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { SafetyAnalyticsActions } from './safety-analytics.actions';
import { AuthActions } from '@app/store/auth/auth.actions';
import { SafetyAnalyticsApiService } from '@app/core/api/safety-analytics';
import { LoggerService } from '@app/core/infrastructure';

@Injectable()
export class SafetyAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly safetyApi = inject(SafetyAnalyticsApiService);
  private readonly logger = inject(LoggerService).scope('SafetyAnalyticsEffects');

  loadSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyAnalyticsActions.loadSummary),
      switchMap(({ params }) =>
        this.safetyApi.getSummary(params).pipe(
          map(summary => SafetyAnalyticsActions.loadSummarySuccess({ summary })),
          catchError(error => {
            this.logger.error('Load safety summary failed:', error);
            return of(SafetyAnalyticsActions.loadSummaryFailure({ error: error.message }));
          })
        )
      )
    )
  );

  loadTrend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyAnalyticsActions.loadTrend),
      switchMap(({ params }) =>
        this.safetyApi.getTrend(params).pipe(
          map(trend => SafetyAnalyticsActions.loadTrendSuccess({ trend })),
          catchError(error => {
            this.logger.error('Load safety trend failed:', error);
            return of(SafetyAnalyticsActions.loadTrendFailure({ error: error.message }));
          })
        )
      )
    )
  );

  loadEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafetyAnalyticsActions.loadEvents),
      switchMap(({ params }) =>
        this.safetyApi.getEvents(params).pipe(
          map(response => SafetyAnalyticsActions.loadEventsSuccess({ events: response.events, totalCount: response.total_count })),
          catchError(error => {
            this.logger.error('Load safety events failed:', error);
            return of(SafetyAnalyticsActions.loadEventsFailure({ error: error.message }));
          })
        )
      )
    )
  );

  clearOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => SafetyAnalyticsActions.clearSafetyAnalyticsState())
    )
  );

  logSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SafetyAnalyticsActions.loadSummarySuccess,
        SafetyAnalyticsActions.loadTrendSuccess,
        SafetyAnalyticsActions.loadEventsSuccess
      ),
      tap(action => this.logger.debug('Success:', action.type))
    ),
    { dispatch: false }
  );
}
